/* s24-motion.js — scroll reveal for the s24 bands.
 *
 * PROGRESSIVE BY CONSTRUCTION. The bands are fully visible in the HTML; this
 * script is what OPTS THEM IN to being hidden-then-revealed, by adding the
 * `s24-reveal` class itself. So if the script fails to load, errors, or is
 * blocked, nothing is ever hidden and the page reads exactly as it does now.
 * The opposite arrangement — markup shipped at opacity 0 waiting for JS — is
 * how a page ends up blank for the people least able to debug it, and it also
 * means an empty first frame for link previews and thumbnails.
 *
 * Reduced motion is honoured twice: here, by not opting in at all, and in CSS,
 * where the whole rule sits behind prefers-reduced-motion: no-preference.
 */
(function () {
  'use strict';

  var reduced = window.matchMedia &&
                window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduced || !('IntersectionObserver' in window)) return;


  /* ---- hero typewriter ------------------------------------------------
   * "BUILT FOR <word>" — types a word in, holds, deletes it, types the next.
   *
   * The first word is ALREADY in the server HTML and the reserved-width box is
   * pure CSS, so if this never runs the hero still reads correctly and at the
   * right size. That is why nothing here needs to set up an initial state.
   *
   * Reduced motion never reaches this function — init() returns early — so a
   * visitor who asks for less motion keeps the static first word.
   */
  function typewriter(root) {
    var live = root.querySelector('.s24-tw-live');
    var raw = root.getAttribute('data-s24-words') || '';
    var words = raw.split(',').map(function (w) { return w.trim(); })
                   .filter(function (w) { return w.length; });
    if (!live || words.length < 2) return;

    var TYPE = 85, DELETE = 45, HOLD = 1900, BETWEEN = 320;
    // Start on the word the server already rendered, so the first transition
    // deletes what is on screen rather than jumping to a different word.
    var idx = Math.max(0, words.indexOf(live.textContent.trim()));
    var chars = words[idx].length;
    var deleting = true;
    var visible = true;
    var timer = null;

    function step() {
      var word = words[idx];
      if (deleting) {
        chars -= 1;
        if (chars <= 0) {
          chars = 0;
          deleting = false;
          idx = (idx + 1) % words.length;
          schedule(BETWEEN);
        } else {
          schedule(DELETE);
        }
      } else {
        chars += 1;
        if (chars >= word.length) {
          chars = word.length;
          deleting = true;
          root.classList.remove('s24-tw-typing');   // let the caret blink again
          schedule(HOLD);
        } else {
          schedule(TYPE);
        }
      }
      live.textContent = words[idx].slice(0, chars);
    }

    function schedule(delay) {
      clearTimeout(timer);
      if (!visible) return;                          // do not queue work off-screen
      root.classList.add('s24-tw-typing');
      timer = setTimeout(step, delay);
    }

    // Off-screen it stops entirely. A hero timer ticking while someone reads
    // the footer is wasted battery, and on a phone that is not free.
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        visible = e.isIntersecting;
        if (visible) {
          schedule(HOLD);
        } else {
          clearTimeout(timer);
          root.classList.remove('s24-tw-typing');
        }
      });
    }, { threshold: 0 });
    io.observe(root);
  }

  function init() {
    Array.prototype.forEach.call(
      document.querySelectorAll('.s24-tw'), typewriter);

    var bands = document.querySelectorAll('.s24-band');
    if (!bands.length) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('s24-in');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.05 });

    Array.prototype.forEach.call(bands, function (band, i) {
      // The first band on a page is already in view on load. Revealing it would
      // mean the page's opening content animates in from nothing every time,
      // which reads as jank rather than polish — so it is left alone.
      if (i === 0) {
        band.classList.add('s24-in');
        return;
      }
      band.classList.add('s24-reveal');
      io.observe(band);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
